import { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import Badge from './Badge';
import PoMoveTool from './PoMoveTool';

// One entry per entity Danger Zone can manage. The backend already
// computes `display_name` for every entity in GET /danger-zone/records,
// so `label` here is mostly cosmetic — search placeholder and modal copy.
const ENTITIES = [
  { key: 'contractors', label: 'Contractors' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'clients', label: 'Clients' },
  { key: 'people', label: 'People' },
  { key: 'work_orders', label: 'Work Orders' },
  { key: 'purchase_orders', label: 'Purchase Orders' },
  { key: 'ar_invoices', label: 'AR Invoices' },
  { key: 'ap_invoices', label: 'AP Invoices' },
];

// Builds the small grey "meta" line shown under each record's name —
// entity-specific so the most relevant numbers surface without crowding
// the table with a different column set per entity.
function getRecordMeta(entity, r) {
  if (entity === 'vendors') {
    return `${r.active_contractor_count ?? 0} active contractor(s) · ${r.invoice_count ?? 0} AP invoice(s)` +
      (r.unpaid_count > 0 ? ` · ⚠ ${r.unpaid_count} unpaid` : '');
  }
  if (entity === 'clients') {
    return `${r.invoice_count ?? 0} AR invoice(s)` + (r.unpaid_count > 0 ? ` · ⚠ ${r.unpaid_count} unpaid` : '');
  }
  if (entity === 'contractors') {
    return `${r.ar_count ?? 0} AR · ${r.ap_count ?? 0} AP` + (r.unpaid_count > 0 ? ` · ⚠ ${r.unpaid_count} unpaid` : '');
  }
  if (entity === 'people') {
    const owed = (r.paid_total ?? 0) + (r.expense_total ?? 0);
    return `${r.wo_count ?? 0} Work Order(s) owned` + (owed > 0 ? ` · $${owed.toFixed(2)} paid/expensed to date` : '');
  }
  if (entity === 'work_orders') {
    return `${r.client_name || '—'} · ${r.contractor_name || '—'} · ${r.po_count ?? 0} PO(s) · ${r.ar_count ?? 0} AR · ${r.ap_count ?? 0} AP`;
  }
  if (entity === 'purchase_orders') {
    return `${r.client_wo_number || '—'} · ${r.contractor_name || '—'} · ${r.ar_count ?? 0} AR · ${r.ap_count ?? 0} AP`;
  }
  if (entity === 'ar_invoices') {
    return `${r.client_name || '—'} · ${r.contractor_name || '—'} · $${(r.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` +
      (r.verified_paid > 0 ? ` · $${r.verified_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })} verified paid` : '');
  }
  if (entity === 'ap_invoices') {
    return `${r.vendor_name || '—'} · ${r.contractor_name || '—'} · $${(r.amount_to_pay ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` +
      (r.verified_paid > 0 ? ` · $${r.verified_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })} verified paid` : '');
  }
  return '';
}

export default function DangerZoneTab() {
  const [entity, setEntity] = useState('contractors');
  const [showDeleted, setShowDeleted] = useState(false);
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // 'soft' | 'hard'
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);

  const entityDef = ENTITIES.find(e => e.key === entity);

  function load() {
    setError(false);
    setSelected([]);
    setResult(null);
    const searchParam = ['ar_invoices', 'ap_invoices'].includes(entity) && search ? `&search=${encodeURIComponent(search)}` : '';
    api.get(`/danger-zone/records?entity=${entity}&show_deleted=${showDeleted}${searchParam}`)
      .then(r => setRecords(r.data))
      .catch(() => setError(true));
  }

  useEffect(load, [entity, showDeleted]);
  // For invoice entities the search is server-side (large tables); for
  // everything else it's instant client-side filtering below.
  useEffect(() => {
    if (['ar_invoices', 'ap_invoices'].includes(entity) && search.length > 2) load();
  }, [search]);

  const filtered = useMemo(() => {
    if (!records) return [];
    if (['ar_invoices', 'ap_invoices'].includes(entity)) return records; // already server-filtered
    if (!search) return records;
    return records.filter(r => (r.display_name || '').toLowerCase().includes(search.toLowerCase()));
  }, [records, search, entity]);

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }
  function toggleAll() {
    setSelected(s => s.length === filtered.length ? [] : filtered.map(r => r.id));
  }

  async function handleSoftDelete() {
    setProcessing(true);
    try {
      const r = await api.post('/danger-zone/soft-delete', { entity, ids: selected });
      setResult({ ...r.data, mode: 'soft' });
      load();
      setConfirmModal(null);
    } catch (e) {
      setResult({ error: e.response?.data?.error || 'Failed' });
    } finally {
      setProcessing(false);
    }
  }

  async function handleRestore() {
    setProcessing(true);
    try {
      const r = await api.post('/danger-zone/restore', { entity, ids: selected });
      setResult({ deleted: r.data.restored, skipped: r.data.skipped, mode: 'restore' });
      load();
    } catch (e) {
      setResult({ error: e.response?.data?.error || 'Failed' });
    } finally {
      setProcessing(false);
    }
  }

  async function handleHardDelete() {
    if (confirmText !== `DELETE ${selected.length} RECORDS`) return;
    setProcessing(true);
    try {
      const r = await api.post('/danger-zone/hard-delete', { entity, ids: selected, confirmation: confirmText });
      setResult({ ...r.data, mode: 'hard' });
      load();
      setConfirmModal(null);
      setConfirmText('');
    } catch (e) {
      setResult({ error: e.response?.data?.error || 'Failed' });
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div>
      <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '12px 16px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
        <strong>Warning:</strong> Soft delete hides records from active counts but keeps them visible everywhere (shown with a "Deleted" badge) so financial history is never silently lost. Hard delete is permanent and only available on records that are already soft-deleted — and is still blocked if anything else still references the record.
      </div>

      {result && (
        <div style={{
          background: result.error ? 'var(--danger-bg)' : 'var(--success-bg)',
          color: result.error ? 'var(--danger-text)' : 'var(--success-text)',
          padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16,
        }}>
          {result.error || `✓ ${result.mode === 'restore' ? 'Restored' : result.mode === 'hard' ? 'Permanently deleted' : 'Soft-deleted'}: ${result.deleted?.length || 0}. Skipped: ${result.skipped?.length || 0}.`}
          {result.skipped?.length > 0 && (
            <ul style={{ marginTop: 6, paddingLeft: 18 }}>
              {result.skipped.map((s, i) => <li key={i}>Record #{s.id}: {s.reason}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {ENTITIES.map(e => (
          <button
            key={e.key}
            className={`btn btn-sm ${entity === e.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setEntity(e.key); setSearch(''); setSelected([]); }}
          >
            {e.label}
          </button>
        ))}
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
          Show soft-deleted only
        </label>
      </div>

      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="filter-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${entityDef.label.toLowerCase()}…`}
          style={{ flex: 1 }}
        />
      </div>

      {selected.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px',
          background: 'var(--danger-bg)', borderRadius: 'var(--radius)', marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger-text)' }}>{selected.length} selected</span>
          {!showDeleted && (
            <button className="btn btn-sm btn-secondary" onClick={() => setConfirmModal('soft')}>Soft Delete</button>
          )}
          {showDeleted && (
            <>
              <button className="btn btn-sm btn-secondary" onClick={handleRestore} disabled={processing}>Restore</button>
              <button className="btn btn-sm" style={{ background: 'var(--danger-text)', color: '#fff', border: 'none' }} onClick={() => { setConfirmModal('hard'); setConfirmText(''); }}>
                Permanently Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-ghost" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      {error && <div className="form-hint" style={{ color: 'var(--danger-text)' }}>Could not load records. <button className="btn btn-secondary btn-sm" onClick={load}>Try again</button></div>}
      {!error && records === null && <div className="form-hint">Loading…</div>}
      {!error && records !== null && filtered.length === 0 && <div className="form-hint">No records found.</div>}

      {!error && filtered.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleAll} />
              </th>
              <th>{entityDef.label.replace(/s$/, '')} Name / Number</th>
              <th>Details</th>
              <th>Status</th>
              {showDeleted && <th>Deleted At</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id}>
                <td><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                <td style={{ fontWeight: 500 }}>{r.display_name || r.invoice_number || `#${r.id}`}</td>
                <td style={{ color: 'var(--muted)', fontSize: 13 }}>{getRecordMeta(entity, r)}</td>
                <td>
                  {r.deleted_at
                    ? <Badge variant="danger">Deleted</Badge>
                    : (r.is_active === 0 || r.status === 'void' || r.status === 'closed')
                      ? <Badge variant="neutral">Inactive</Badge>
                      : <Badge variant="success">Active</Badge>}
                </td>
                {showDeleted && <td style={{ fontSize: 12, color: 'var(--muted)' }}>{r.deleted_at ? new Date(r.deleted_at).toLocaleString() : '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmModal === 'soft' && (
        <div className="panel-overlay" onClick={() => setConfirmModal(null)}>
          <div className="quick-panel" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">Confirm Soft Delete</h3>
              <button className="drawer-close" onClick={() => setConfirmModal(null)}>×</button>
            </div>
            <div className="panel-body">
              <p style={{ marginBottom: 12 }}>You are about to soft-delete <strong>{selected.length} {entityDef.label.toLowerCase()}</strong>.</p>
              <p className="form-hint" style={{ marginBottom: 20 }}>
                Records will stay visible with a "Deleted" badge but be excluded from active counts. This can be reversed any time. Records with unpaid invoices or active dependencies will be skipped and listed.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSoftDelete} disabled={processing}>{processing ? 'Processing…' : 'Confirm Soft Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmModal === 'hard' && (
        <div className="panel-overlay" onClick={() => setConfirmModal(null)}>
          <div className="quick-panel" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title" style={{ color: 'var(--danger-text)' }}>⚠ Permanent Deletion</h3>
              <button className="drawer-close" onClick={() => setConfirmModal(null)}>×</button>
            </div>
            <div className="panel-body">
              <p style={{ marginBottom: 12, color: 'var(--danger-text)', fontWeight: 600 }}>
                This action is irreversible. {selected.length} record{selected.length > 1 ? 's' : ''} will be permanently deleted.
              </p>
              <p className="form-hint" style={{ marginBottom: 16 }}>
                Records still referenced by other data (e.g. a Client with Work Orders, an Invoice with payments) will be skipped and listed.
              </p>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label>Type <strong>DELETE {selected.length} RECORDS</strong> to confirm</label>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={`DELETE ${selected.length} RECORDS`}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  style={{ background: 'var(--danger-text)', opacity: confirmText === `DELETE ${selected.length} RECORDS` ? 1 : 0.5 }}
                  onClick={handleHardDelete}
                  disabled={processing || confirmText !== `DELETE ${selected.length} RECORDS`}
                >
                  {processing ? 'Deleting…' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PoMoveTool />
    </div>
  );
}
